import { DotLottieWorker } from '@lottiefiles/dotlottie-web';
import { addWindowResizeEvent } from '@theme/utilities';
import { fetchAnimationData, getCurrentAnimationData } from './data';

type OptionsProps = {
	autoplay?: boolean;
	loop?: boolean;
	triggerElement?: string;
	onLoad?: (dotLottie: DotLottieWorker | null) => void;
	pauseOnExit?: boolean;
	workerId?: string;
};

export class Animation {
	private canvas: HTMLCanvasElement;
	private src: string;
	private autoplay: boolean;
	private loop: boolean;
	private onLoad: (dotLottie: DotLottieWorker | null) => void;
	private pauseOnExit: boolean;
	private workerId: string;
	private dotLottie: DotLottieWorker | null = null;
	private intersectionObserver: IntersectionObserver;
	private dataLight: string | null = null;
	private dataDark: string | null = null;
	private aspectRatio: string | null = null;

	constructor(canvas: HTMLCanvasElement, src: string, options: OptionsProps = {}) {
		const { autoplay = false, loop = false, triggerElement = '', onLoad = function () {}, pauseOnExit = true, workerId = 'default' } = options;
		this.canvas = canvas;
		this.src = src;

		// Options
		this.autoplay = autoplay;
		this.loop = loop;
		this.onLoad = onLoad;
		this.pauseOnExit = pauseOnExit;
		this.workerId = workerId;

		document.addEventListener('colorModeChange', this.handleColorModeChange);

		// Check for triggers
		if (triggerElement) {
			document.querySelectorAll(triggerElement).forEach((trigger) => {
				trigger.addEventListener('mouseenter', () => {
					this.play();
				});
			});
		}

		// Observe the canvas element
		this.intersectionObserverCallback = this.intersectionObserverCallback.bind(this);
		this.intersectionObserver = new IntersectionObserver(this.intersectionObserverCallback, {
			rootMargin: '400px',
		});
		this.intersectionObserver.observe(this.canvas);

		addWindowResizeEvent(() => this.resize());
	}

	private get currentAnimationData(): string | null {
		return getCurrentAnimationData({ dataLight: this.dataLight || '', dataDark: this.dataDark || '' });
	}

	private handleColorModeChange = (): void => {
		const data = this.currentAnimationData;
		if (!this.dotLottie || !data) {
			return;
		}

		this.dotLottie.load({
			data,
			autoplay: this.autoplay,
			loop: this.loop,
			layout: {
				fit: 'contain',
				align: this.aspectRatio ? [0, 0] : [0.5, 0.5],
			},
		});
	};

	initAnimation = async () => {
		const response = await fetchAnimationData(this.src);
		if (!response) {
			this.intersectionObserver.unobserve(this.canvas);
			return;
		}
		const { dataLight, dataDark, aspectRatio } = response;

		this.aspectRatio = aspectRatio || null;
		this.dataLight = dataLight;
		this.dataDark = dataDark || null;
		const data = this.currentAnimationData;

		// If data is false, unobserve the current entry and exit early
		if (!data) {
			this.intersectionObserver.unobserve(this.canvas);
			return;
		}

		// Set the aspect ratio
		if (aspectRatio) {
			this.canvas.style.setProperty('--aspect-ratio', aspectRatio);
			this.canvas.classList.add('known-aspect-ratio');
		}

		this.dotLottie = new DotLottieWorker({
			canvas: this.canvas,
			data,
			autoplay: this.autoplay,
			loop: this.loop,
			workerId: this.workerId,
			layout: {
				fit: 'contain',
				align: aspectRatio ? [0, 0] : [0.5, 0.5],
			},
		});

		// On load callback
		this.dotLottie.addEventListener('load', () => {
			this.canvas.classList.add('loaded');
			this.onLoad(this.dotLottie);
		});
	};

	intersectionObserverCallback(entries: IntersectionObserverEntry[]) {
		entries.forEach((entry) => {
			if (entry.isIntersecting) {
				if (this.dotLottie === null) {
					this.initAnimation();
				} else if (this.autoplay) {
					this.dotLottie.play();
				}
			} else if (this.dotLottie !== null && this.autoplay && this.dotLottie.isPlaying && this.pauseOnExit) {
				this.dotLottie.pause();
			}
		});
	}

	play() {
		this.dotLottie?.play();
	}

	pause() {
		this.dotLottie?.pause();
	}

	stop() {
		this.dotLottie?.stop();
	}

	resize() {
		this.dotLottie?.resize();
	}
}
